/**
 * Iter 67cz — Location autocomplete (vendor side).
 *
 * Two modes:
 *   • multi (default) — chip-based picker. Vendors add several pickup or
 *     drop-off destinations from the central catalog. Drop-in replacement
 *     for the legacy free-text `DestinationChips`.
 *   • single — pick exactly one location. Backs the car's main `location`
 *     field (formerly a free-text Input that produced "Tirana" /
 *     "tirana" / "Tirane" duplicates).
 *
 * The picker calls `/api/locations/catalog?q=…` (the same endpoint the
 * renter-side mobile search uses) so the entire platform converges on
 * one canonical list. Vendors *cannot* type free-text — every chip /
 * value must be selected from the dropdown so the catalog truly becomes
 * the single source of truth.
 *
 * Selected values are stored as the location's display `name` (e.g.
 * "Tirana Airport") — same shape as before, so all downstream APIs
 * (`pickup_locations[]`, `dropoff_locations[]`, `location`) continue to
 * work unchanged.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { X, MapPin, Plane, Anchor, Building2, TreePalm, Loader2, ChevronDown, Search } from "lucide-react";

const KIND_ICON = {
  airport: Plane,
  port: Anchor,
  city: Building2,
  tourist: TreePalm,
  hotel_area: Building2,
};

function KindIcon({ kind, className = "h-3.5 w-3.5" }) {
  const I = KIND_ICON[kind] || MapPin;
  return <I className={className} />;
}

export default function LocationAutocomplete({
  mode = "multi",          // "multi" | "single"
  value,                    // array<string> in multi, string in single
  onChange,                 // (next) => void
  placeholder = "Type a city, airport or port…",
  testIdPrefix = "loc",
  disabled = false,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Normalise: in single-mode `value` is a string. The input doubles as a
  // display field when there's a value — vendor clicks ✕ to clear.
  const singleSelected = mode === "single" ? (value || "").trim() : null;
  const multiSelected = mode === "multi" ? (Array.isArray(value) ? value : []) : null;

  // Debounced lookup.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/locations/catalog", {
          params: { q: query.trim(), limit: 12 },
        });
        if (cancelled) return;
        // Hide chips already selected (multi mode).
        const filtered = (data || []).filter((r) =>
          mode === "multi" ? !multiSelected.includes(r.name) : true
        );
        setResults(filtered);
        setActiveIdx(0);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 180);
    return () => { cancelled = true; clearTimeout(t); };
  // multiSelected changes on every render but we want a fresh exclusion
  // list — stringify it for a stable dep.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open, JSON.stringify(multiSelected)]);

  // Click-outside to close.
  useEffect(() => {
    const onDoc = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const choose = (loc) => {
    if (!loc) return;
    if (mode === "multi") {
      if (multiSelected.includes(loc.name)) return;
      onChange([...multiSelected, loc.name]);
      setQuery("");
      // Keep the panel open so vendors can stack picks rapidly.
      setOpen(true);
      inputRef.current?.focus();
    } else {
      onChange(loc.name);
      setQuery("");
      setOpen(false);
    }
  };

  const clearSingle = () => { onChange(""); setQuery(""); setOpen(true); inputRef.current?.focus(); };
  const removeChip = (idx) => onChange(multiSelected.filter((_, i) => i !== idx));

  const onKey = (e) => {
    if (!open || results.length === 0) {
      if (e.key === "ArrowDown") { setOpen(true); }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(results[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showInput = mode === "multi" || !singleSelected;

  // Common dropdown UI.
  const dropdown = useMemo(() => {
    if (!open) return null;
    return (
      <div
        className="absolute z-30 mt-1.5 w-full bg-white border border-border rounded-2xl shadow-2xl shadow-neutral-900/10 overflow-hidden"
        data-testid={`${testIdPrefix}-dropdown`}
      >
        {loading && (
          <div className="px-4 py-3 text-sm text-neutral-500 flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
          </div>
        )}
        {!loading && results.length === 0 && (
          <div className="px-4 py-4 text-sm text-neutral-500" data-testid={`${testIdPrefix}-no-results`}>
            {query.trim()
              ? <>No matching destination. Try a shorter search or ask the platform admin to add it.</>
              : <>Start typing to see destinations from the platform catalog.</>}
          </div>
        )}
        {!loading && results.length > 0 && (
          <ul className="max-h-72 overflow-y-auto py-1" role="listbox">
            {results.map((r, i) => (
              <li
                key={r.slug}
                onMouseDown={(e) => { e.preventDefault(); choose(r); }}
                onMouseEnter={() => setActiveIdx(i)}
                className={`px-3.5 py-2 text-sm cursor-pointer flex items-center gap-2.5 ${
                  i === activeIdx ? "bg-indigo-50 text-indigo-900" : "hover:bg-neutral-50 text-neutral-800"
                }`}
                role="option"
                aria-selected={i === activeIdx}
                data-testid={`${testIdPrefix}-result-${r.slug}`}
              >
                <span className="h-7 w-7 rounded-full bg-neutral-100 text-neutral-600 flex items-center justify-center shrink-0">
                  <KindIcon kind={r.kind} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="font-medium block truncate">{r.name}</span>
                  <span className="text-[11px] text-neutral-500 uppercase tracking-wider">
                    {r.kind} · {r.country}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }, [open, loading, results, query, activeIdx, testIdPrefix]);

  return (
    <div className="space-y-2" ref={containerRef}>
      {/* ── Selected value(s) display ─────────────────────────────────── */}
      {mode === "multi" && multiSelected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {multiSelected.map((v, i) => (
            <span
              key={`${v}-${i}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 px-3 py-1 text-sm"
              data-testid={`${testIdPrefix}-chip-${i}`}
            >
              <MapPin className="h-3 w-3" />
              {v}
              <button
                type="button"
                onClick={() => removeChip(i)}
                className="hover:bg-indigo-100 rounded-full p-0.5"
                aria-label={`Remove ${v}`}
                data-testid={`${testIdPrefix}-chip-remove-${i}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* ── Input row ─────────────────────────────────────────────────── */}
      <div className="relative">
        {showInput ? (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400 pointer-events-none" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onKeyDown={onKey}
              placeholder={placeholder}
              disabled={disabled}
              className="rounded-xl pl-8.5 pr-9"
              style={{ paddingLeft: 34 }}
              data-testid={`${testIdPrefix}-input`}
              autoComplete="off"
              aria-expanded={open}
              aria-haspopup="listbox"
            />
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400 pointer-events-none" />
          </div>
        ) : (
          // single-mode, already chosen — show the chosen value with a clear ✕
          <div
            className="flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-2 text-sm"
            data-testid={`${testIdPrefix}-selected`}
          >
            <MapPin className="h-3.5 w-3.5 text-indigo-600" />
            <span className="flex-1 truncate font-medium text-neutral-900">{singleSelected}</span>
            <button
              type="button"
              onClick={clearSingle}
              className="text-neutral-400 hover:text-neutral-700"
              aria-label="Clear location"
              data-testid={`${testIdPrefix}-clear`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        {dropdown}
      </div>
    </div>
  );
}
